import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

function renderSectionBody(bodyText: string) {
  const lines = bodyText.split('\n').filter(line => line.trim() !== '');
  return lines.map((line, i) => {
    const bulletMatch = line.match(/^[•\d.]+/);
    if (line.startsWith('•') || (bulletMatch && /^\d+\.\d+/.test(bulletMatch[0]))) {
      const content = line.replace(/^(?:•\s*|\d+\.\d+\s*)/, '');
      return (
        <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <span style={{ color: 'var(--accent)', marginTop: 2, flexShrink: 0 }}>{line.startsWith('•') ? '•' : bulletMatch?.[0]}</span>
          <span>{content}</span>
        </li>
      );
    }
    return <p key={i} style={i === 0 ? undefined : { marginTop: 16 }}>{line}</p>;
  });
}

export default function PrivacyPage() {
  const { t } = useTranslation("home");
  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--page-bg)",
        display: "flex",
        flexDirection: "column",
        fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
        color: "var(--text-secondary)",
      }}
    >
      <style>{`
        .pp-header {
          background-color: var(--page-bg);
          border-bottom: 1px solid var(--border);
          position: relative;
        }
        .pp-header-inner {
          max-width: 1280px;
          margin: 0 auto;
          padding: 20px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .pp-back {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          color: var(--text-secondary);
          text-decoration: none;
          white-space: nowrap;
          cursor: pointer;
        }
        .pp-back:hover { color: var(--text-primary); }
        .pp-title-wrap {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .pp-title {
          font-size: 18px;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0;
        }
        .pp-subtitle {
          font-size: 12px;
          color: var(--text-secondary);
          margin-top: 2px;
        }
        .pp-placeholder { width: 80px; }

        .pp-main {
          flex: 1;
          max-width: 768px;
          width: 100%;
          margin: 0 auto;
          padding: 40px 24px 64px;
        }
        @media (min-width: 640px) {
          .pp-main { padding: 64px 40px 80px; }
          .pp-title { font-size: 20px; }
        }

        .pp-banner {
          position: relative;
          border-radius: 16px;
          overflow: hidden;
          margin-bottom: 40px;
        }
        .pp-banner img {
          width: 100%;
          height: 200px;
          object-fit: cover;
          opacity: 0.5;
          display: block;
        }
        .pp-banner-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(31,16,48,1), rgba(31,16,48,0.6), rgba(31,16,48,0));
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 0 24px;
        }
        .pp-banner-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: rgba(201,169,110,0.1);
          border: 1px solid rgba(201,169,110,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 12px;
        }
        .pp-banner-icon i { font-size: 24px; color: #c9a96e; }
        .pp-banner-title {
          font-size: 18px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 8px;
        }
        .pp-banner-desc {
          font-size: 14px;
          color: #c9c9d5;
          margin: 0;
        }

        .pp-badges {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 40px;
        }
        @media (min-width: 640px) { .pp-badges { gap: 16px; } }
        .pp-badge {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          border-radius: 16px;
          border: 1px solid var(--border);
          background: var(--card-bg);
          padding: 16px 20px;
        }
        .pp-badge-icon {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: var(--accent-soft);
          border: 1px solid var(--accent-border);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .pp-badge-icon i { font-size: 20px; color: var(--accent); }
        .pp-badge-label {
          font-size: 12px;
          font-weight: 500;
          color: var(--text-secondary);
        }

        .pp-card {
          border-radius: 16px;
          border: 1px solid var(--border);
          background: var(--card-bg);
          padding: 24px;
          margin-bottom: 24px;
        }
        .pp-card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }
        .pp-card-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: var(--accent-soft);
          border: 1px solid var(--accent-border);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .pp-card-icon i { font-size: 16px; color: var(--accent); }
        .pp-card-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0;
        }
        .pp-card-body {
          font-size: 14px;
          line-height: 1.75;
          color: var(--text-secondary);
        }
        .pp-card-body p { margin: 0 0 12px; }
        .pp-card-body ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .pp-card-body li {
          display: flex;
          gap: 8px;
          align-items: flex-start;
        }
        .pp-bullet {
          color: var(--accent);
          margin-top: 2px;
          flex-shrink: 0;
        }

        .pp-contact {
          text-align: center;
        }
        .pp-contact-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: var(--accent-soft);
          border: 1px solid var(--accent-border);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 12px;
        }
        .pp-contact-icon i { font-size: 24px; color: var(--accent); }
        .pp-contact-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0 0 4px;
        }
        .pp-contact-desc {
          font-size: 14px;
          color: var(--text-secondary);
          margin: 0 0 16px;
        }
        .pp-contact-email {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          color: var(--accent-hover);
          text-decoration: none;
        }
        .pp-contact-email:hover { color: var(--accent); }
        .pp-contact-note {
          font-size: 12px;
          color: var(--text-secondary);
          margin-top: 12px;
        }
      `}</style>

      {/* Header */}
      <header className="pp-header">
        <div className="pp-header-inner">
          <Link to="/" className="pp-back">
            <i className="ri-arrow-left-s-line" style={{ fontSize: 18 }} />
            <span>{t("privacy.back_home")}</span>
          </Link>
          <div className="pp-title-wrap">
            <h1 className="pp-title">{t("privacy.title")}</h1>
            <span className="pp-subtitle">{t("privacy.subtitle")}</span>
          </div>
          <div className="pp-placeholder" />
        </div>
      </header>

      <main className="pp-main">
        {/* Banner */}
        <div className="pp-banner">
          <img
            src="/images/misc/12fe45517b6c1600d35a3dbc4bb53cf2.jpg"
            alt={t("privacy.banner_alt")}
          />
          <div className="pp-banner-overlay">
            <div className="pp-banner-icon">
              <i className="ri-shield-check-fill" />
            </div>
            <h2 className="pp-banner-title">{t("privacy.banner_title")}</h2>
            <p className="pp-banner-desc">{t("privacy.banner_desc")}</p>
          </div>
        </div>

        {/* Badges */}
        <div className="pp-badges">
          <div className="pp-badge">
            <div className="pp-badge-icon">
              <i className="ri-lock-password-line" />
            </div>
            <span className="pp-badge-label">{t("privacy.badge1")}</span>
          </div>
          <div className="pp-badge">
            <div className="pp-badge-icon">
              <i className="ri-prohibited-line" />
            </div>
            <span className="pp-badge-label">{t("privacy.badge2")}</span>
          </div>
          <div className="pp-badge">
            <div className="pp-badge-icon">
              <i className="ri-user-heart-line" />
            </div>
            <span className="pp-badge-label">{t("privacy.badge3")}</span>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="pp-card">
          <div className="pp-card-header">
            <i className="ri-error-warning-line" style={{ fontSize: 16, color: "var(--accent)" }} />
            <h3 className="pp-card-title">{t("privacy.disclaimer_title")}</h3>
          </div>
          <div className="pp-card-body">
            <p>
              {t("privacy.disclaimer_p1")}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: "var(--accent-soft)",
                    border: "1px solid var(--accent-border)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                >
                  <i className="ri-shield-check-line" style={{ fontSize: 14, color: "var(--accent)" }} />
                </div>
                <span style={{ fontSize: 14, lineHeight: 1.75, color: "var(--text-secondary)" }}>
                  {t("privacy.disclaimer_b1")}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: "var(--accent-soft)",
                    border: "1px solid var(--accent-border)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                >
                  <i className="ri-compass-3-line" style={{ fontSize: 14, color: "var(--accent)" }} />
                </div>
                <span style={{ fontSize: 14, lineHeight: 1.75, color: "var(--text-secondary)" }}>
                  {t("privacy.disclaimer_b2")}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: "var(--accent-soft)",
                    border: "1px solid var(--accent-border)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                >
                  <i className="ri-heart-pulse-line" style={{ fontSize: 14, color: "var(--accent)" }} />
                </div>
                <span style={{ fontSize: 14, lineHeight: 1.75, color: "var(--text-secondary)" }}>
                  {t("privacy.disclaimer_b3")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 一、信息收集 */}
        <div className="pp-card">
          <div className="pp-card-header">
            <div className="pp-card-icon">
              <i className="ri-database-2-line" />
            </div>
            <h3 className="pp-card-title">{t("privacy.sec1.title")}</h3>
          </div>
          <div className="pp-card-body">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {renderSectionBody(t("privacy.sec1.body"))}
            </ul>
          </div>
        </div>

        {/* 二、信息使用 */}
        <div className="pp-card">
          <div className="pp-card-header">
            <div className="pp-card-icon">
              <i className="ri-settings-4-line" />
            </div>
            <h3 className="pp-card-title">{t("privacy.sec2.title")}</h3>
          </div>
          <div className="pp-card-body">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {renderSectionBody(t("privacy.sec2.body"))}
            </ul>
          </div>
        </div>

        {/* 三、信息共享 */}
        <div className="pp-card">
          <div className="pp-card-header">
            <div className="pp-card-icon">
              <i className="ri-share-line" />
            </div>
            <h3 className="pp-card-title">{t("privacy.sec3.title")}</h3>
          </div>
          <div className="pp-card-body">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {renderSectionBody(t("privacy.sec3.body"))}
            </ul>
          </div>
        </div>

        {/* 四、信息安全 */}
        <div className="pp-card">
          <div className="pp-card-header">
            <div className="pp-card-icon">
              <i className="ri-shield-check-line" />
            </div>
            <h3 className="pp-card-title">{t("privacy.sec4.title")}</h3>
          </div>
          <div className="pp-card-body">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {renderSectionBody(t("privacy.sec4.body"))}
            </ul>
          </div>
        </div>

        {/* 五、Cookie 与追踪技术 */}
        <div className="pp-card">
          <div className="pp-card-header">
            <div className="pp-card-icon">
              <i className="ri-file-code-line" />
            </div>
            <h3 className="pp-card-title">{t("privacy.sec5.title")}</h3>
          </div>
          <div className="pp-card-body">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {renderSectionBody(t("privacy.sec5.body"))}
            </ul>
          </div>
        </div>

        {/* 六、您的权利 */}
        <div className="pp-card">
          <div className="pp-card-header">
            <div className="pp-card-icon">
              <i className="ri-user-settings-line" />
            </div>
            <h3 className="pp-card-title">{t("privacy.sec6.title")}</h3>
          </div>
          <div className="pp-card-body">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {renderSectionBody(t("privacy.sec6.body"))}
            </ul>
          </div>
        </div>

        {/* 七、未成年人保护 */}
        <div className="pp-card">
          <div className="pp-card-header">
            <div className="pp-card-icon">
              <i className="ri-parent-line" />
            </div>
            <h3 className="pp-card-title">{t("privacy.sec7.title")}</h3>
          </div>
          <div className="pp-card-body">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {renderSectionBody(t("privacy.sec7.body"))}
            </ul>
          </div>
        </div>

        {/* 八、数据保留 */}
        <div className="pp-card">
          <div className="pp-card-header">
            <div className="pp-card-icon">
              <i className="ri-time-line" />
            </div>
            <h3 className="pp-card-title">{t("privacy.sec8.title")}</h3>
          </div>
          <div className="pp-card-body">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {renderSectionBody(t("privacy.sec8.body"))}
            </ul>
          </div>
        </div>

        {/* 九、隐私政策更新 */}
        <div className="pp-card">
          <div className="pp-card-header">
            <div className="pp-card-icon">
              <i className="ri-refresh-line" />
            </div>
            <h3 className="pp-card-title">{t("privacy.sec9.title")}</h3>
          </div>
          <div className="pp-card-body">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {renderSectionBody(t("privacy.sec9.body"))}
            </ul>
          </div>
        </div>

        {/* Contact */}
        <div className="pp-card pp-contact">
          <div className="pp-contact-icon">
            <i className="ri-mail-line" />
          </div>
          <p className="pp-contact-title">{t("privacy.contact_title")}</p>
          <p className="pp-contact-desc">
            {t("privacy.contact_desc")}
          </p>
          <a href="mailto:dazhuotech@gmail.com" className="pp-contact-email">
            <i className="ri-mail-line" style={{ fontSize: 14, color: "var(--accent-hover)" }} />
            dazhuotech@gmail.com
          </a>
          <p className="pp-contact-note">{t("privacy.contact_note")}</p>
        </div>
      </main>
    </div>
  );
}